import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { DistributionStrip } from './DistributionStrip'
import { GapWaterfall } from './GapWaterfall'
import { BenchmarkSources } from './BenchmarkSources'
import { useBenchmarkLabels } from './useBenchmarkLabels'
import { assetLabel, formatGap, formatKPIValue, pick, type Strip } from './benchmark-utils'
import { EmptyState } from '../ui/empty-state'

/**
 * The building view: where it stands line by line, why the gap, and what is
 * behind a line (spec 2026-09-13-01).
 */
interface Props {
    org: string
    assetUid: string
    windowEnd?: string
    locale: string
}

// The reading order of a building's lines: the headline cost first, its split
// by trade next, then the other measures.
const STRIP_ORDER = [
    'building_opex_per_unit_12m',
    'building_trade_opex_per_unit_12m',
    'building_opex_per_area_12m',
    'building_corrective_share_12m',
]

function stripRank(s: Strip) {
    const i = STRIP_ORDER.indexOf(s.kpi_code)
    return i === -1 ? STRIP_ORDER.length : i
}

function stripKey(s: Strip) {
    return `${s.kpi_code}|${s.breakdown_key}|${s.subject.uid}`
}

export function AssetBenchmarkView({ org, assetUid, windowEnd, locale }: Props) {
    const { t } = useTranslation('benchmark')
    const { byCode, lang, tradeLabel, kpiLabel } = useBenchmarkLabels(org)
    const [selected, setSelected] = useState<string | null>(null)

    const stripsQuery = $api.useQuery('get', '/api/v1/organizations/{orgCode}/benchmark/assets/{assetUid}', {
        params: { path: { orgCode: org, assetUid }, query: { window_end: windowEnd } },
    })
    const decompositionQuery = $api.useQuery('get', '/api/v1/organizations/{orgCode}/benchmark/assets/{assetUid}/decomposition', {
        params: { path: { orgCode: org, assetUid }, query: { window_end: windowEnd } },
    })

    if (stripsQuery.isLoading) {
        return <div className="h-64 animate-pulse rounded-md bg-muted" data-testid="benchmark-asset-loading" />
    }
    if (stripsQuery.isError || !stripsQuery.data) {
        return <p className="text-sm text-destructive">{t('error')}</p>
    }
    const { strips, asset } = stripsQuery.data
    const buildingStrips = strips
        .filter((s) => s.subject.uid === asset.uid)
        .sort((a, b) => stripRank(a) - stripRank(b) || a.breakdown_key.localeCompare(b.breakdown_key))
    const equipmentStrips = strips.filter((s) => s.subject.uid !== asset.uid)
    const selectedStrip = strips.find((s) => stripKey(s) === selected) ?? null

    const rightText = (s: Strip) => {
        if (s.excluded_reason) return t('strips.excluded', { reason: t(`reason.${s.excluded_reason}`) })
        if (s.cohort.suppressed) return t('strips.suppressed', { count: s.cohort.n })
        const gap = formatGap(s.delta_vs_median, locale)
        // Without a median to measure against (a cohort all at zero) the rank
        // alone is the reading — never a dangling "· —".
        const withGap = (text: string) => (s.delta_vs_median === null ? text.replace(/ · —$/, '') : text)
        if (!s.rank) return s.delta_vs_median !== null ? t('strips.vs_median', { gap }) : ''
        if (s.rank.decile !== null) {
            if (s.outlier === 'high' || s.rank.decile === 10) return withGap(t('strips.above_d9', { gap }))
            if (s.rank.decile === 1) return withGap(t('strips.below_d1', { gap }))
            return withGap(t('strips.decile', { decile: s.rank.decile, gap }))
        }
        const key = s.rank.position === 1 ? 'strips.position_first' : 'strips.position'
        return withGap(t(key, { position: s.rank.position, of: s.rank.of, gap }))
    }

    const renderStrip = (s: Strip) => {
        const def = byCode.get(s.kpi_code)
        const unit = def?.unit ?? 'currency'
        const fmt = (v: number) => formatKPIValue(unit, v, s.currency, locale)
        // A trade line is titled by its trade: the KPI name is the same on all of
        // them and truncation would cut away the only word that differs.
        const title = s.breakdown_key ? tradeLabel(s.breakdown_key) : kpiLabel(s.kpi_code)
        const kpiPrefix = s.breakdown_key ? `${kpiLabel(s.kpi_code)} · ` : ''
        const denominator = def ? pick(def.denominator.label, lang) : ''
        const coverage = s.quality.allocation_coverage
        const key = stripKey(s)
        const testId = `benchmark-strip-${s.kpi_code}${s.breakdown_key ? `-${s.breakdown_key}` : ''}${s.subject.uid !== asset.uid ? `-${s.subject.code || s.subject.uid}` : ''}`
        const isOutlier = s.outlier === 'high'
        return (
            <button
                type="button"
                key={key}
                onClick={() => setSelected(selected === key ? null : key)}
                aria-pressed={selected === key}
                className={`grid w-full grid-cols-1 items-center gap-x-4 gap-y-1 rounded-md px-2 py-2 text-left hover:bg-muted/50 md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)_minmax(0,11rem)] ${selected === key ? 'bg-muted/60' : ''}`}
                data-testid={testId}
            >
                <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{title}</div>
                    <div className="truncate text-xs text-muted-foreground">
                        {kpiPrefix}
                        {s.subject.uid !== asset.uid && <span className="mr-1">{assetLabel(s.subject)} ·</span>}
                        {s.value !== null ? `${fmt(s.value)}${unit === 'currency' && denominator ? ` / ${denominator}` : ''}` : '—'}
                    </div>
                    <div className="text-[11px] text-muted-foreground" data-testid={`${testId}-n`}>
                        n = {s.cohort.n}
                        {coverage !== null && ` · ${new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(coverage)}`}
                    </div>
                </div>
                <DistributionStrip
                    value={s.value}
                    stats={s.cohort.suppressed ? null : (s.stats ?? null)}
                    peerValues={s.peer_values}
                    outlier={s.outlier}
                    label={title}
                    formatValue={fmt}
                    testId={`${testId}-chart`}
                />
                <div className={`flex items-center gap-2 text-sm tabular-nums md:justify-end ${isOutlier ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                    {isOutlier && <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-warning" />}
                    <span data-testid={`${testId}-reading`}>{rightText(s)}</span>
                    {s.open_issue_uid && <Badge variant="secondary">{t('ranked.open_issue')}</Badge>}
                </div>
            </button>
        )
    }

    return (
        <div className="space-y-4" data-testid="benchmark-asset-view">
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">{t('strips.title')}</CardTitle>
                    <p className="text-xs text-muted-foreground">{t('strips.subtitle')}</p>
                </CardHeader>
                <CardContent className="space-y-1">
                    {strips.length === 0 && <EmptyState variant="inline" live title={t('strips.no_strips')} />}
                    {buildingStrips.map(renderStrip)}
                    {equipmentStrips.length > 0 && (
                        <>
                            <h4 className="px-2 pt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('strips.equipment')}</h4>
                            {equipmentStrips.map(renderStrip)}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* A ratio read from a materialized state has no line-level source in
                this window: the server refuses it, the card is not offered. */}
            {selectedStrip && byCode.get(selectedStrip.kpi_code)?.attribution !== 'state' && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">{t('sources.title')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <BenchmarkSources
                            org={org}
                            subjectUid={selectedStrip.subject.uid}
                            metric={selectedStrip.kpi_code}
                            breakdownKey={selectedStrip.breakdown_key}
                            windowEnd={windowEnd}
                            currency={selectedStrip.currency}
                        />
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">{t('waterfall.title')}</CardTitle>
                    <p className="text-xs text-muted-foreground">{t('waterfall.subtitle')}</p>
                </CardHeader>
                <CardContent data-testid="benchmark-waterfall">
                    {decompositionQuery.data?.excluded_reason ? (
                        <p className="text-sm text-muted-foreground">
                            {t('waterfall.excluded', { reason: t(`reason.${decompositionQuery.data.excluded_reason}`) })}
                        </p>
                    ) : decompositionQuery.data && decompositionQuery.data.median !== null && decompositionQuery.data.steps.length > 0 ? (
                        <GapWaterfall decomposition={decompositionQuery.data} tradeLabel={tradeLabel} locale={locale} />
                    ) : (
                        <p className="text-sm text-muted-foreground">{t('waterfall.unavailable')}</p>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
